/**
 * Findings Page - Compliance Gaps and Security Vulnerabilities
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, CheckCircle2 } from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FindingsPage() {
  const [findings, setFindings] = useState([]);
  const [pentestFindings, setPentestFindings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchFindings = async () => {
      try {
        const [findingsRes, summaryRes, pentestRes] = await Promise.all([
          fetch(`${API_URL}/api/findings/?limit=50`, { credentials: 'include' }),
          fetch(`${API_URL}/api/findings/summary`, { credentials: 'include' }),
          fetch(`${API_URL}/api/pentest/reports`, { credentials: 'include' }),
        ]);
        
        if (findingsRes.ok) setFindings((await findingsRes.json()).findings || []);
        if (summaryRes.ok) setSummary(await summaryRes.json());
        
        if (pentestRes.ok) {
          const pentestData = await pentestRes.json();
          const allPentestFindings = [];
          for (const report of pentestData.reports || []) {
            const reportRes = await fetch(`${API_URL}/api/pentest/reports/${report.report_id}`, {
              credentials: 'include', headers
            });
            if (reportRes.ok) {
              const reportData = await reportRes.json();
              (reportData.findings || []).forEach(f => {
                allPentestFindings.push({
                  ...f,
                  source: 'pentest',
                  report_label: report.label || report.filename,
                  report_id: report.report_id,
                });
              });
            }
          }
          setPentestFindings(allPentestFindings);
        }
      } catch (error) {
        console.error('Failed to fetch findings:', error);
        toast.error('Failed to load findings');
      } finally {
        setLoading(false);
      }
    };
    fetchFindings();
  }, []);

  const displayFindings = activeTab === 'all' 
    ? [...findings, ...pentestFindings.map(pf => ({
        finding_id: `PT-${pf.title?.slice(0, 20)}`,
        title: pf.title,
        severity: pf.severity,
        status: 'open',
        environment: 'Security Scan',
        source: 'pentest',
        cvss_score: pf.cvss_score,
        control_ids: pf.control_ids,
      }))]
    : activeTab === 'compliance' 
      ? findings 
      : pentestFindings.map(pf => ({
          finding_id: `PT-${pf.title?.slice(0, 20)}`,
          title: pf.title,
          severity: pf.severity,
          status: 'open',
          environment: 'Security Scan',
          source: 'pentest',
          cvss_score: pf.cvss_score,
          control_ids: pf.control_ids,
        }));

  return (
    <div className="findings-page">
      <div className="page-header">
        <div>
          <h1>Findings Tracker</h1>
          <p>Track and remediate compliance gaps and security vulnerabilities</p>
        </div>
        <button className="btn-primary" data-testid="create-finding-btn">
          <Plus size={16} /> Create Finding
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card critical">
          <span className="summary-label">Critical/High Open</span>
          <span className="summary-value">
            {(summary?.open_critical || 0) + pentestFindings.filter(f => f.severity === 'critical' || f.severity === 'high').length}
          </span>
        </div>
        <div className="summary-card warning">
          <span className="summary-label">Compliance Findings</span>
          <span className="summary-value">{findings.length}</span>
        </div>
        <div className="summary-card info">
          <span className="summary-label">Security Scan Findings</span>
          <span className="summary-value">{pentestFindings.length}</span>
        </div>
        <div className="summary-card success">
          <span className="summary-label">Resolved</span>
          <span className="summary-value">{summary?.by_status?.resolved || 0}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="findings-tabs">
        <button 
          className={activeTab === 'all' ? 'active' : ''} 
          onClick={() => setActiveTab('all')}
          data-testid="tab-all-findings"
        >
          All Findings ({findings.length + pentestFindings.length})
        </button>
        <button 
          className={activeTab === 'compliance' ? 'active' : ''} 
          onClick={() => setActiveTab('compliance')}
          data-testid="tab-compliance-findings"
        >
          Compliance ({findings.length})
        </button>
        <button 
          className={activeTab === 'security' ? 'active' : ''} 
          onClick={() => setActiveTab('security')}
          data-testid="tab-security-findings"
        >
          Security Scans ({pentestFindings.length})
        </button>
      </div>

      <div className="findings-list">
        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : displayFindings.length > 0 ? (
          displayFindings.map((finding, idx) => (
            <Card key={`${finding.finding_id}-${idx}`} className="finding-card">
              <div className="finding-header">
                <div>
                  <div className="finding-id">
                    {finding.source === 'pentest' ? (
                      <Badge color="#7c3aed" style={{ marginRight: 8 }}>Security Scan</Badge>
                    ) : null}
                    {finding.finding_id}
                  </div>
                  <div className="finding-title">{finding.title}</div>
                  <div className="finding-meta">
                    {finding.environment || 'Unknown'} 
                    {finding.cvss_score ? ` — CVSS: ${finding.cvss_score}` : ''}
                    {finding.control_ids?.length ? ` — Controls: ${finding.control_ids.join(', ')}` : ''}
                    {finding.assigned_to ? ` — Assigned to ${finding.assigned_to}` : ''}
                  </div>
                </div>
                <div className="finding-badges">
                  <Badge color={
                    finding.severity === 'critical' ? '#dc2626' : 
                    finding.severity === 'high' ? '#ea580c' : 
                    finding.severity === 'medium' ? '#d97706' :
                    finding.severity === 'low' ? '#FF00BE' : '#6b7280'
                  }>
                    {finding.severity}
                  </Badge>
                  <Badge color={
                    finding.status === 'resolved' ? '#059669' :
                    finding.status === 'in_progress' ? '#d97706' : '#dc2626'
                  }>{finding.status?.replace(/_/g, ' ') || 'open'}</Badge>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="empty-state">
            <CheckCircle2 size={48} color="#059669" />
            <h3>No findings</h3>
            <p>Great job! No compliance gaps have been identified.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
